import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';
import './Home.css';

import Lottie from 'lottie-react';

import icon1 from '../assets/icons/icon_1.svg';
import icon2 from '../assets/icons/icon_2.svg';
import icon3 from '../assets/icons/icon_3.svg';
import animationData from '../assets/icons/spy-anim.json';
import mainImage from '../assets/img/main.png';
import { analyzeProfile, fullAnalyze, getProfileId, getSession, getUser } from '../services/Api';

const Home = () => {
  const [profileLink, setProfileLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [sessionData, setSessionData] = useState(null); // Состояние для данных сессии
  const location = useLocation();
  const shouldHideBlocks = location.state?.hideBlocks || false;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const loadingTexts = [
    'Полный анализ профиля занимает в среднем 3-4 минуты, ожидайте...',
    'Все ещё анализируем, наберитесь терпения или перейдите в личный кабинет...',
    'Идет обработка данных, пожалуйста, подождите...',
    'Анализ профиля в процессе, это может занять некоторое время....'
  ];

  useEffect(() => {
    let interval;
    if (isLoading) {
      interval = setInterval(() => {
        setCurrentTextIndex(prevIndex => (prevIndex + 1) % loadingTexts.length);
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [isLoading, loadingTexts.length]);

  useEffect(() => {
    document.body.style.overflow = isLoading ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isLoading]);

  useEffect(() => {
    console.log('Текущее состояние sessionData:', sessionData); // Для отладки
  }, [sessionData]);

  const extractUsername = link => {
    if (!link) return '';
    return link.split('/').pop();
  };

  const checkUserAuthorization = async () => {
    try {
      const user = await getUser();
      if (user && user.subExpiresAt > Math.floor(Date.now() / 1000)) {
        return user;
      }
    } catch (error) {
      console.log('Гость, пропускаем получение данных о пользователе');
    }
    return null;
  };

  const fetchProfileId = async username => {
    try {
      const profileId = await getProfileId(username);
      if (!profileId) {
        throw new Error('Не удалось получить ID пользователя');
      }
      return profileId;
    } catch (error) {
      throw new Error(error.message || 'Ошибка при получении ID профиля');
    }
  };

  const pollSession = async profileId => {
    let attempts = 0;
    const maxAttempts = 200;
    const pollInterval = 2000;

    while (attempts < maxAttempts) {
      try {
        const session = await getSession(profileId);
        setSessionData(session); // Сохраняем данные сессии на каждой итерации
        if (session.data?.status === 'completed') {
          return session.data;
        }
        attempts++;
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (error) {
        throw new Error('Ошибка при получении статуса анализа');
      }
    }
    throw new Error('Анализ не завершился в течение ожидаемого времени');
  };

  const runFullAnalysis = async profileId => {
    try {
      const startResponse = await fullAnalyze(profileId);
      if (!startResponse.success) {
        throw new Error(startResponse.message || 'Не удалось запустить анализ');
      }
      return await pollSession(profileId);
    } catch (error) {
      throw new Error(error.message || 'Ошибка при запуске полного анализа');
    }
  };

  const runFreeAnalysis = async profileId => {
    try {
      const analysisResponse = await analyzeProfile(profileId);
      setSessionData(analysisResponse); // Сохраняем сразу
      if (analysisResponse.data?.status === 'completed' || analysisResponse.message === 'Анализ уже был проведен') {
        return analysisResponse.data;
      }
      if (!analysisResponse.success) {
        throw new Error(analysisResponse.message || 'Лимит анализов исчерпан или произошла ошибка');
      }
      let attempts = 0;
      const maxAttempts = 20;
      const pollInterval = 2000;
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        const updatedAnalysis = await analyzeProfile(profileId);
        setSessionData(updatedAnalysis); // Сохраняем на каждой итерации
        if (updatedAnalysis.data?.status === 'completed') {
          return updatedAnalysis.data;
        }
        attempts++;
      }
      throw new Error('Бесплатный анализ не завершился в ожидаемое время');
    } catch (error) {
      throw new Error(error.message || 'Ошибка при бесплатном анализе');
    }
  };

  const handleSubmit = async profileLink => {
    if (!profileLink) return;

    // Сначала полностью сбрасываем прогресс и состояние загрузки
    setIsLoading(false);
    setProgress(0);
    setSessionData(null);

    // Добавляем небольшую задержку перед началом новой загрузки
    // чтобы React успел обновить состояние
    setTimeout(() => {
      setIsLoading(true);

      // Флаг для отслеживания типа анализа
      let isFullAnalysis = false;

      const processAnalysis = async () => {
        try {
          const username = extractUsername(profileLink);
          const profileId = await fetchProfileId(username);

          let user = null;
          try {
            user = await checkUserAuthorization();
          } catch (error) {
            console.log('Гость, продолжаем с бесплатным анализом');
          }

          isFullAnalysis = !!user;
          setSessionData(prev => ({ ...prev, isFullAnalysis }));

          const totalTime = isFullAnalysis ? 300 : 10; // в секундах

          // Создаем функцию для плавного заполнения прогресс-бара
          const animateProgress = duration => {
            const startTime = Date.now();
            const endTime = startTime + duration;

            let animationFrameId = null;

            const updateProgressBar = () => {
              const now = Date.now();
              const timeElapsed = now - startTime;

              if (timeElapsed < duration && isLoading) {
                const completionRatio = timeElapsed / duration;
                const newProgress = Math.min(
                  98,
                  Math.round(completionRatio * completionRatio * (3 - 2 * completionRatio) * 100)
                );

                setProgress(newProgress);
                animationFrameId = requestAnimationFrame(updateProgressBar);
              } else {
                cancelAnimationFrame(animationFrameId);
              }
            };

            animationFrameId = requestAnimationFrame(updateProgressBar);

            // Функция для очистки анимации
            return () => {
              if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
              }
            };
          };

          // Запускаем анимацию прогресс-бара и сохраняем функцию для её очистки
          const cleanupAnimation = animateProgress(totalTime * 1000);

          // Запускаем реальный анализ параллельно
          const analysisPromise = isFullAnalysis ? runFullAnalysis(profileId) : runFreeAnalysis(profileId);

          // Ожидаем завершения анализа
          const analysisData = await analysisPromise;

          // Получаем последние данные сессии
          const session = await getSession(profileId);
          setSessionData({ ...session, isFullAnalysis });

          if (analysisData?.status === 'completed') {
            // Остановка анимации
            cleanupAnimation();

            // Когда анализ завершен, устанавливаем прогресс 100%
            setProgress(100);
            await new Promise(resolve => setTimeout(resolve, 800));

            window.scrollTo({ top: 0, behavior: 'smooth' });
            navigate('/result', {
              state: {
                profileData: analysisData,
                timestamp: Date.now()
              }
            });
          } else {
            console.log('Анализ не завершен, но проверяем sessionData:', session);
            setIsLoading(false);
          }
        } catch (error) {
          let errorProgress = progress;
          const slowProgress = setInterval(() => {
            if (errorProgress < 95) {
              errorProgress += 1;
              setProgress(errorProgress);
            } else {
              clearInterval(slowProgress);
            }
          }, 300);

          toast.error(error.message || 'Произошла ошибка при обработке запроса', {
            position: 'top-right',
            autoClose: 5000
          });

          setTimeout(() => {
            setIsLoading(false);
            clearInterval(slowProgress);
            // Явно сбрасываем прогресс после ошибки
            setProgress(0);
          }, 3000);
        }
      };

      processAnalysis();
    }, 50); // Небольшая задержка перед началом новой попытки
  };

  useEffect(() => {
    if (searchParams.has('profileLink')) {
      setProfileLink(searchParams.get('profileLink'));
      handleSubmit(searchParams.get('profileLink'));
    }
  }, [searchParams]);

  const toggleAnswer = e => {
    const faqItem = e.target.closest('.faq__item');
    faqItem.classList.toggle('active');
  };

  return (
    <div>
      <section className="profile-analysis">
        <div className="profile-analysis__container">
          <div className="profile-analysis__image">
            <img src={mainImage} height={680} alt="Главное изображение для анализа профиля" />
          </div>
          <div className="profile-analysis__form">
            {isLoading ? (
              <div className="loading-screen">
                <Lottie animationData={animationData} loop={true} />
                <p>{loadingTexts[currentTextIndex]}</p>
                <p>Идет анализ: {progress}%</p>
                <div className="progress-bar">
                  <div className="progress-bar__fill" style={{ width: `${progress}%` }}></div>
                </div>
                {/* Показываем кнопку только если текущий анализ - полный (не бесплатный) */}
                {currentTextIndex >= 1 && sessionData?.isFullAnalysis && (
                  <button
                    className="profile-analysis__button mt-4"
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      navigate('/profile');
                    }}
                  >
                    Перейти в личный кабинет
                  </button>
                )}
              </div>
            ) : (
              <>
                <p className="profile-analysis__description">
                  Введите ссылку <br />
                  на профиль VK <br />
                  для анализа:
                </p>
                <form
                  onSubmit={e => {
                    e.preventDefault();
                    handleSubmit(profileLink);
                  }}
                >
                  <input
                    type="text"
                    className="profile-analysis__input"
                    placeholder="Например: vk.com/id236587"
                    value={profileLink}
                    onChange={e => setProfileLink(e.target.value)}
                  />
                  <button type="submit" className="profile-analysis__button">
                    Начать анализ
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </section>

      {!shouldHideBlocks && (
        <section className="service-description">
          <div className="service-description__container" id="about">
            <h2 className="service-description__title">Spygram</h2>
            <p className="service-description__text">
              — это инновационный сервис, созданный для анализа страниц пользователей ВКонтакте. Он предоставляет юзерам
              доступ к важной информации об аккаунтах, включая активность пользователя, лайки, скрытых друзей и анализ
              друзей и взаимодействий в социальной сети. В условиях растущего интереса к анализу данных в социальных
              медиа, Spygram предлагает мощные инструменты для понимания и оптимизации взаимодействия в сети. Мы
              обеспечиваем пользователей уникальными аналитическими данными, которые могут помочь как в личных целях,
              так и в бизнесе.
            </p>
            <p className="service-description__text">
              Spygram работает в рамках закона, гарантируя конфиденциальность и безопасность данных пользователей.
              Интуитивно понятный интерфейс делает наш сервис доступным для широкого круга пользователей, независимо от
              уровня их технической подготовки. Наши алгоритмы собирают и обрабатывают информацию с максимальной
              эффективностью, позволяя пользователям получить полное представление о своих друзьях и подписчиках.
            </p>
          </div>
        </section>
      )}

      {!shouldHideBlocks && (
        <section className="features">
          <div className="features__container" id="advantage">
            <div className="feature">
              <div className="feature__icon">
                <img src={icon1} alt="Иконка друзей" />
              </div>
              <div className="feature__content">
                <h3 className="feature__title">Анализ друзей</h3>
                <p className="feature__description">
                  Функция поиска скрытых друзей позволяет пользователям видеть список своих друзей, которые по
                  каким-либо причинам скрыты от общего доступа. Используя алгоритмы анализа данных, Spygram выявляет
                  тех, кто скрыл свои профили или перечень друзей, оптимизируя информацию для пользователя.
                  <br />
                  <br />
                  Эта возможность помогает лучше понять социальный круг общения и выявить значимых людей, которые могли
                  быть упущены ранее. Кроме того, пользователи могут оценить, сколько скрытых друзей у них есть, и какие
                  из них наиболее активны.
                </p>
              </div>
            </div>

            <div className="feature">
              <div className="feature__icon">
                <img src={icon2} alt="Иконка активности" />
              </div>
              <div className="feature__content">
                <h3 className="feature__title">Активности</h3>
                <p className="feature__description">
                  С помощью функции проверки активности пользователя, Spygram предоставляет детальную информацию о том,
                  как часто и каким образом аккаунт взаимодействует с другими пользователями. Сервис анализирует
                  регулярность публикаций, лайков, комментариев и других действий.
                  <br />
                  <br />
                  Это помогает определить уровень вовлеченности пользователя, а также понять, насколько активно он
                  использует платформу. Такая информация может быть полезна для оценки собственного позиций в соцсетях
                  или для анализа интересов друзей.
                </p>
              </div>
            </div>

            <div className="feature">
              <div className="feature__icon">
                <img src={icon3} alt="Иконка скрытых друзей" />
              </div>
              <div className="feature__content">
                <h3 className="feature__title">Предполагаемые скрытые друзья</h3>
                <p className="feature__description">
                  Функция поиска скрытых друзей позволяет пользователям видеть список своих друзей, которые по
                  каким-либо причинам скрыты от общего доступа. Используя алгоритмы анализа данных, Spygram выявляет
                  тех, кто скрыл свои профили или перечень друзей, оптимизируя информацию для пользователя.
                  <br />
                  <br />
                  Эта возможность помогает лучше понять социальный круг общения и выявить значимых людей, которые могли
                  быть упущены ранее. Кроме того, пользователи могут оценить, сколько скрытых друзей у них есть, и какие
                  из них наиболее активны.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {!shouldHideBlocks && (
        <section className="faq">
          <div className="faq__container" id="faq">
            <h2 className="faq__title">
              <span>FAQ:</span> Ответы на часто задаваемые вопросы
            </h2>
            <div className="faq__item">
              <div className="faq__question" onClick={toggleAnswer}>
                <h3>
                  <span>1</span> Что такое Spygram?
                </h3>
                <div className="faq__arrow"></div>
              </div>
              <div className="faq__answer">
                <p>
                  Spygram — это сервис, который анализирует страницы ВКонтакте, предоставляя пользователям доступ к
                  информации об активности, взаимодействиях и скрытых друзьях.
                </p>
              </div>
            </div>
            <div className="faq__item">
              <div className="faq__question" onClick={toggleAnswer}>
                <h3>
                  <span>2.</span> Как работает поиск скрытых друзей?
                </h3>
                <div className="faq__arrow"></div>
              </div>
              <div className="faq__answer">
                <p>
                  Функция поиска скрытых друзей позволяет пользователям видеть список своих друзей, которые по
                  каким-либо причинам скрыты от общего доступа.
                </p>
              </div>
            </div>
            <div className="faq__item">
              <div className="faq__question" onClick={toggleAnswer}>
                <h3>
                  <span>3.</span> В чем заключается проверка активности пользователя?
                </h3>
                <div className="faq__arrow"></div>
              </div>
              <div className="faq__answer">
                <p>
                  С помощью функции проверки активности пользователя, Spygram предоставляет детальную информацию о том,
                  как часто и каким образом аккаунт взаимодействует с другими пользователями.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
